/**
 * ProtectedRoute Component
 * Coláiste Lurgan Management System - Route protection with role-based access
 */

import { Navigate, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuth, useUserPermissions } from '../../hooks/useAuth';
import { LoadingScreen } from '../ui/loading';
import AccessDenied from '../pages/AccessDenied';
import api from '../../lib/api';
import { useState, useEffect } from 'react';

export function ProtectedRoute({
  children,
  requiredPermissions = [],
  requiredUserTypes = [],
  strictUserTypes = [],  // Enforced even for ADMIN — use for parent-only routes
  pageKey = null,
  fallbackPath = '/login'
}) {
  const location = useLocation();
  const { isAuthenticated, isLoading, isInitialized } = useAuth();
  const { hasAnyPermission, userType, isLoading: permissionsLoading } = useUserPermissions();
  const [showColdStartTip, setShowColdStartTip] = useState(false);

  // Fetch accessible pages from Page Permissions API
  const { data: accessiblePagesData, isLoading: pagesLoading } = useQuery({
    queryKey: ['user', 'pages'],
    queryFn: api.pagePermissions.getMyPages,
    staleTime: 5 * 60 * 1000,
    enabled: isAuthenticated && !!userType,
  });

  // Show cold start tip after 3 seconds of loading
  useEffect(() => {
    if (isLoading || !isInitialized || permissionsLoading || pagesLoading) {
      const timer = setTimeout(() => {
        setShowColdStartTip(true);
      }, 3000);

      return () => clearTimeout(timer);
    } else {
      setShowColdStartTip(false);
    }
  }, [isLoading, isInitialized, permissionsLoading, pagesLoading]);

  // Show loading while checking authentication
  if (isLoading || !isInitialized || permissionsLoading || pagesLoading) {
    return (
      <LoadingScreen
        message="Ag seiceáil ceadúnas... / Checking authorization..."
        showTip={showColdStartTip}
      />
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to={fallbackPath} state={{ from: location }} replace />;
  }

  // strictUserTypes is enforced before the ADMIN bypass (for parent-only routes)
  if (strictUserTypes.length > 0 && !strictUserTypes.includes(userType)) {
    return (
      <AccessDenied
        requiredUserTypes={strictUserTypes}
        userType={userType}
      />
    );
  }

  // ADMIN users have access to everything else - bypass all permission checks
  if (userType === 'ADMIN') {
    return children;
  }

  // Check Page Permissions (database-driven) if pageKey is provided
  const accessiblePages = accessiblePagesData?.pages || [];
  if (pageKey && accessiblePages.length > 0) {
    // Page Permissions API has loaded - use it as the source of truth
    if (!accessiblePages.includes(pageKey)) {
      // Redirect PARENT users away from dashboard to their own portal
      if (userType === 'PARENT' && pageKey === 'dashboard') {
        return <Navigate to="/family-dashboard" replace />;
      }
      return (
        <AccessDenied
          requiredPermissions={[`access_${pageKey}_page`]}
          userType={userType}
        />
      );
    }
    // Page access granted via Page Permissions - skip Django permission checks
    return children;
  }

  // Fallback: Check user type requirements (when Page Permissions not configured)
  if (requiredUserTypes.length > 0 && !requiredUserTypes.includes(userType)) {
    return (
      <AccessDenied
        requiredUserTypes={requiredUserTypes}
        userType={userType}
      />
    );
  }

  // Fallback: Check Django permission requirements
  if (requiredPermissions.length > 0 && !hasAnyPermission(requiredPermissions)) {
    return (
      <AccessDenied
        requiredPermissions={requiredPermissions}
        userType={userType}
      />
    );
  }

  // User is authenticated and authorized
  return children;
}

export default ProtectedRoute;